import {FC, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {api, Announcement, Booking, DoorCode, ApiError, formatEvery} from '../../services/api'
import {useToast} from '../../components/Toast'
import {confirmDialog} from '../../components/dialog'
import {Spinner} from '../../components/Spinner'
import {Surface, SectionTitle} from './ui'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

/**
 * Private access details for approved renters (brief §6). Exact address and
 * key-fob location are intentionally NOT on any public page — they live here,
 * behind login + an active reservation.
 *
 * TODO: source these from admin-editable Info Pages / door-code notes rather
 * than constants once §7/§13 land. Values are verbatim from the first-edit brief.
 */
export const PRIVATE_INFO = {
  address: '1390 Market Street, Apt 2105, San Francisco, CA 94102',
  entry: 'Dial 852 on the call box for first entry — your key fob then opens this door and the Polk Street access. Take the elevator to the 21st floor, turn left out of the elevator; Apt 2105 is down the hall on the left. Check-in details are shared before arrival, and check-in does not require speaking with security.',
  transport: {
    car: 'Approx. 20 minutes to/from SFO by car',
    transit: 'Approx. 45 minutes to/from SFO by public transit',
    disclaimer: 'Times vary with traffic, service frequency, transfers, and time of day.',
  },
  cleaning: {
    price: '$5 per set',
    provider: 'Vanda P.',
    memo: 'The fee is charged to your card on file and is separate from rent and the refundable deposit.',
  },
  fridge: 'Label all food with your name and date using the markers provided. The cleaning service may discard expired, spoiled, unlabeled, or abandoned items.',
  departure: 'Remove your belongings and food, clear your bed area, and lock the door behind you. Return your personal key fob — the one on the keychain with your miniature boarding pass — to its hook in the hallway where you collected it.',
  wifi: {network: 'Minshew Mania', password: 'Peartree1!'},
  // Order matters: 911 first, security LAST — renters were reaching for the
  // security desk before the people who can actually help them.
  contacts: {
    emergency: '911',
    urgent: '510-904-4141',
    email: 'support@crosscheckcrashpad.com',
    security: '415-559-6763',
    maintenance: 'For non-urgent issues, use the Cleaning & Maintenance page to submit them.',
  },
}

const mapsHref = (q: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`

/**
 * Predominant Current Access Passcode header — shown at the top of the signed-in
 * landing page, only to approved renters with an active reservation. Includes a
 * copy button and a do-not-share warning. The primary passcode is the door code
 * with the lowest sort order; any others are listed beneath it.
 */
export const PasscodeHeader = ({codes}: {codes: DoorCode[]}) => {
  const toast = useToast()
  const [copied, setCopied] = useState(false)
  if (codes.length === 0) return null

  const sorted = [...codes].sort((a, b) => a.sortOrder - b.sortOrder)
  const primary = sorted[0]
  const extra = sorted.slice(1)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(primary.code)
      setCopied(true)
      toast.success('Passcode copied')
      setTimeout(() => setCopied(false), 1800)
    } catch {
      toast.error('Could not copy — copy it manually')
    }
  }

  return (
    <div
      className='ccp-fade-up'
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #012a16 0%, #024d27 55%, #039540 100%)',
        color: '#fff',
        borderRadius: 18,
        padding: '24px 26px',
        marginBottom: 24,
        boxShadow: '0 12px 30px rgba(1,42,22,0.28)',
      }}
    >
      <div className='ccp-blob' style={{background: '#0ad66a', width: 200, height: 200, top: -120, right: '8%'}} />
      <div style={{position: 'relative'}}>
        <div style={{fontSize: 12, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85}}>
          🔑 Current Access Passcode
        </div>
        <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, margin: '12px 0 6px'}}>
          <div style={{fontSize: 'clamp(34px, 7vw, 48px)', fontWeight: 800, letterSpacing: '0.12em', lineHeight: 1, fontVariantNumeric: 'tabular-nums'}}>
            {primary.code}
          </div>
          <button
            onClick={copy}
            style={{display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: ACCENT, border: 'none', borderRadius: 999, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer'}}
          >
            {copied ? '✓ Copied' : 'Copy passcode'}
          </button>
        </div>
        <div style={{fontSize: 14, opacity: 0.92, fontWeight: 600}}>{primary.label}</div>
        {extra.length > 0 && (
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.2)'}}>
            {extra.map((c) => (
              <div key={c.id}>
                <div style={{fontSize: 12, opacity: 0.8, fontWeight: 600}}>{c.label}</div>
                <div style={{fontSize: 18, fontWeight: 800, letterSpacing: '0.08em'}}>{c.code}</div>
              </div>
            ))}
          </div>
        )}
        <p style={{margin: '16px 0 0', fontSize: 12.5, lineHeight: 1.5, background: 'rgba(255,255,255,0.14)', borderRadius: 10, padding: '9px 12px'}}>
          ⚠️ Do not share this passcode. It is for approved active renters only and is updated every time a renter permanently leaves.
        </p>
      </div>
    </div>
  )
}

const InfoCard: React.FC<{icon: string; title: string; children: React.ReactNode}> = ({icon, title, children}) => (
  <Surface lift style={{padding: '20px 22px', height: '100%'}}>
    <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10}}>
      <span style={{width: 38, height: 38, borderRadius: 10, background: 'rgba(3,149,64,0.1)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 19}}>{icon}</span>
      <h3 style={{fontSize: 15.5, fontWeight: 800, color: INK, margin: 0}}>{title}</h3>
    </div>
    <div style={{fontSize: 13.5, lineHeight: 1.6, color: MUTED}}>{children}</div>
  </Surface>
)

const cardLink: React.CSSProperties = {display: 'inline-block', marginTop: 10, color: ACCENT, fontWeight: 700, fontSize: 13, textDecoration: 'none'}

/**
 * Announcements & current house updates card — shows the most relevant recent
 * update (pinned first) with a link to the full feed. Surfaces the existing
 * announcements feature on the dashboard, per the brief's private cards list.
 */
const AnnouncementsCard = () => {
  const [latest, setLatest] = useState<Announcement | null | undefined>(undefined)
  useEffect(() => {
    let alive = true
    api
      .publicAnnouncements()
      .then((all) => {
        if (!alive) return
        // Pinned first, then most recent by published/created date.
        const sorted = [...all].sort((a, b) => {
          if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
          return +new Date(b.publishedAt ?? b.createdAt) - +new Date(a.publishedAt ?? a.createdAt)
        })
        setLatest(sorted[0] ?? null)
      })
      .catch(() => alive && setLatest(null))
    return () => {
      alive = false
    }
  }, [])

  return (
    <InfoCard icon='📣' title='Announcements & house updates'>
      {latest === undefined ? (
        'Loading the latest updates…'
      ) : latest === null ? (
        'No announcements right now — check back here for current house updates.'
      ) : (
        <>
          {latest.pinned && <span style={{display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '2px 8px', color: ACCENT, background: 'rgba(3,149,64,0.12)', marginBottom: 6}}>📌 Pinned</span>}
          <div style={{fontWeight: 700, color: INK, fontSize: 14, marginBottom: 3}}>{latest.title}</div>
          <div style={{display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{latest.body}</div>
        </>
      )}
      <br />
      <Link to='/announcements' style={cardLink}>All announcements →</Link>
    </InfoCard>
  )
}

/** Wi-Fi card — network + password with a copy button, renter-gated. */
const WifiCard = () => {
  const toast = useToast()
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PRIVATE_INFO.wifi.password)
      setCopied(true)
      toast.success('Wi-Fi password copied')
      setTimeout(() => setCopied(false), 1800)
    } catch {
      toast.error('Could not copy — copy it manually')
    }
  }
  return (
    <InfoCard icon='📶' title='Wi-Fi'>
      <div><span style={{color: MUTED}}>Network</span> · <strong style={{color: INK}}>{PRIVATE_INFO.wifi.network}</strong></div>
      <div style={{marginTop: 4}}><span style={{color: MUTED}}>Password</span> · <strong style={{color: INK, fontVariantNumeric: 'tabular-nums'}}>{PRIVATE_INFO.wifi.password}</strong></div>
      <button onClick={copy} style={{marginTop: 10, fontWeight: 700, fontSize: 13, color: ACCENT, background: 'rgba(3,149,64,0.1)', border: '1px solid rgba(3,149,64,0.25)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer'}}>
        {copied ? '✓ Copied' : 'Copy password'}
      </button>
    </InfoCard>
  )
}

const fmtDateTime = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'})
}

/**
 * Permanent-departure card with the prominent key-fob warning and the renter's
 * "Confirm permanent departure" action (brief §9). Confirming records the
 * timestamp and pings the admin to check the renter out + refund the deposit.
 */
const DepartureCard = ({booking}: {booking?: Booking}) => {
  const toast = useToast()
  const [confirmedAt, setConfirmedAt] = useState<string | null>(booking?.departureConfirmedAt ?? null)
  const [busy, setBusy] = useState(false)
  const canConfirm = !!booking && booking.status === 'APPROVED' && !booking.checkedOutAt

  const confirm = async () => {
    if (!booking) return
    const ok = await confirmDialog({
      title: 'Confirm permanent departure?',
      text: 'Confirm that you have removed your belongings and food, cleared your bed area, locked the door, returned your personal key fob to its hook in the hallway, and hung the blue community gym fob back up. An admin will then complete your check-out and process your refundable deposit.',
      confirmText: 'Yes, I have departed',
    })
    if (!ok) return
    setBusy(true)
    try {
      const updated = await api.confirmDeparture(booking.id)
      setConfirmedAt(updated.departureConfirmedAt)
      toast.success('Departure confirmed — an admin will complete your check-out.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not confirm departure')
    } finally {
      setBusy(false)
    }
  }

  return (
    <InfoCard icon='🔑' title='Permanent departure'>
      {PRIVATE_INFO.departure}
      {/* Prominent key-fob warning (brief §9). */}
      <div style={{marginTop: 12, background: 'rgba(217,33,78,0.06)', border: '1px solid rgba(217,33,78,0.35)', borderRadius: 10, padding: '10px 12px', color: '#9f1239', fontSize: 12.5, lineHeight: 1.5}}>
        <div style={{fontWeight: 800, letterSpacing: '0.04em', marginBottom: 2}}>⚠️ KEY-FOB WARNING</div>
        Your personal key fob — on the keychain with your miniature boarding pass — must go back on its hook in the hallway where you picked it up. Failure to return it may delay your refundable deposit. Please also check the blue community gym fob is hung back up.
      </div>
      <Link to='/info/permanent-departure' style={cardLink}>Departure checklist →</Link>
      {confirmedAt ? (
        <div style={{marginTop: 12, background: 'rgba(3,149,64,0.08)', border: '1px solid rgba(3,149,64,0.3)', borderRadius: 10, padding: '10px 12px', color: ACCENT, fontSize: 13, fontWeight: 700}}>
          ✓ Departure confirmed · {fmtDateTime(confirmedAt)}
          <div style={{fontWeight: 500, color: MUTED, marginTop: 3}}>An admin will complete your check-out and refund.</div>
        </div>
      ) : canConfirm ? (
        <button
          onClick={confirm}
          disabled={busy}
          style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, width: '100%', marginTop: 12, fontWeight: 700, fontSize: 14, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '11px 0', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1}}
        >
          {busy ? <Spinner size={14} /> : 'Confirm permanent departure'}
        </button>
      ) : null}
    </InfoCard>
  )
}

/** The private access cards grid (location, entry, transport, cleaning, fridge, departure). */
/** Cleaning & linens — states the auto-wash policy, with the fee and interval
 *  read from Payment Settings so the copy can't drift from what actually
 *  charges. Falls back to the fee alone if the settings call fails. */
const CleaningCard: FC = () => {
  const [fee, setFee] = useState<string | null>(null)
  const [washEvery, setWashEvery] = useState<string | null>(null)
  useEffect(() => {
    api
      .paymentMethods()
      .then((m) => {
        setFee(Number(m.bedWashFee).toFixed(2))
        setWashEvery(formatEvery(m.bedWashIntervalValue, m.bedWashIntervalUnit))
      })
      .catch(() => {})
  }, [])
  const price = fee ? `$${fee} per set` : PRIVATE_INFO.cleaning.price
  return (
    <InfoCard icon='🧺' title='Cleaning & linens'>
      Optional bed-sheet washing — {price}, handled by {PRIVATE_INFO.cleaning.provider.replace(/\.$/, '')}. Request it in the portal and it goes straight to housekeeping.
      {' '}{PRIVATE_INFO.cleaning.memo}
      {washEvery && (
        <div style={{marginTop: 8}}>
          For hygiene, every bed is washed at least once <strong>{washEvery}</strong>. If you haven’t requested one in that window, we’ll wash it and charge the {price.split(' ')[0]} to your card automatically.
        </div>
      )}
      <br />
      <Link to='/requests' style={cardLink}>Cleaning & maintenance →</Link>
    </InfoCard>
  )
}

export const PrivateAccessCards = ({booking}: {booking?: Booking}) => {
  const bedCode = booking?.bed?.code
  const t = PRIVATE_INFO.transport
  return (
    <div style={{marginTop: 28}}>
      <SectionTitle>Your private access information</SectionTitle>
      <div className='row g-3'>
        <div className='col-12 col-md-6 col-lg-4'>
          <InfoCard icon='📍' title='Your CrashPad location'>
            {PRIVATE_INFO.address}
            <br />
            <a href={mapsHref(PRIVATE_INFO.address)} target='_blank' rel='noopener noreferrer' style={cardLink}>Open in Maps →</a>
          </InfoCard>
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <InfoCard icon='🚪' title='Building entry'>{PRIVATE_INFO.entry}</InfoCard>
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <InfoCard icon='✈️' title='To & from SFO'>
            {t.car}.<br />{t.transit}.
            <div style={{fontSize: 12, marginTop: 6}}>{t.disclaimer}</div>
            <Link to='/info/transport-guide' style={cardLink}>Getting To &amp; From SFO →</Link>
          </InfoCard>
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <CleaningCard />
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <InfoCard icon='🧊' title='Refrigerator etiquette'>
            {PRIVATE_INFO.fridge}
            <br />
            <Link to='/info/refrigerator-etiquette' style={cardLink}>Read details →</Link>
          </InfoCard>
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <DepartureCard booking={booking} />
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <AnnouncementsCard />
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <WifiCard />
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <InfoCard icon='🏠' title='House guide'>
            Entry & comfort, bathroom door, and TV / Fire Stick instructions for the crashpad.
            <br />
            <Link to='/info/house-guide' style={cardLink}>Open house guide →</Link>
          </InfoCard>
        </div>
        <div className='col-12 col-md-6 col-lg-4'>
          <InfoCard icon='🆘' title='Emergency & contacts'>
            <div><span style={{color: MUTED}}>Emergencies (fire/medical)</span> · <strong style={{color: INK}}>{PRIVATE_INFO.contacts.emergency}</strong></div>
            <div style={{marginTop: 4}}><span style={{color: MUTED}}>Urgent issues</span> · <a href={`tel:${PRIVATE_INFO.contacts.urgent}`} style={{color: ACCENT, fontWeight: 700, textDecoration: 'none'}}>{PRIVATE_INFO.contacts.urgent}</a></div>
            <div style={{marginTop: 4}}><span style={{color: MUTED}}>Report an issue</span> · <a href={`mailto:${PRIVATE_INFO.contacts.email}`} style={{color: ACCENT, fontWeight: 700, textDecoration: 'none'}}>{PRIVATE_INFO.contacts.email}</a></div>
            <div style={{marginTop: 4}}><span style={{color: MUTED}}>Security</span> · <a href={`tel:${PRIVATE_INFO.contacts.security}`} style={{color: ACCENT, fontWeight: 700, textDecoration: 'none'}}>{PRIVATE_INFO.contacts.security}</a></div>
            <div style={{marginTop: 6, fontSize: 12.5}}>{PRIVATE_INFO.contacts.maintenance} <Link to='/requests' style={cardLink}>Submit an issue →</Link></div>
          </InfoCard>
        </div>
      </div>
    </div>
  )
}
